<script>
  import {
    Image as ImageIcon,
    LoaderCircle,
    MapPin,
    Mic,
    MoreVertical,
    Paperclip,
    Pause,
    Play,
    Send,
    Smile,
    Trash2,
    X
  } from 'lucide-svelte';
  import { onDestroy, onMount } from 'svelte';
  import { get } from 'svelte/store';
  import {
    conversationId,
    myId,
    replyingTo,
    sendMessage,
    sendTyping,
    uploadFile,
    uploadImages
  } from '$lib/websocket-client';
  import { hasLink } from '$lib/utils';
  import { notify } from '@components/toast/index.js';

  export let onShowStickerPicker;
  export let onShowToolMenu = null;
  export let toolMenuOpen = false;
  export let placeholder = 'Nhập tin nhắn...';
  export let disabled = false;
  export let autoFocus = false;

  const MAX_RECORDING_SECONDS = 300;
  const MIN_RECORDING_SECONDS = 0.7;
  const WAVEFORM_BARS = 36;
  const HOLD_DELAY_MS = 240;

  let text = '';
  let textInput;
  let imageInput;
  let fileInput;
  let previewAudio;
  let toolMenuButton;

  let sending = false;
  let uploading = false;
  let locating = false;
  let typingTimeout = null;
  let isTyping = false;

  let voiceMode = false;
  let recorderState = 'idle';
  let recorderError = '';
  let mediaRecorder = null;
  let mediaStream = null;
  let audioContext = null;
  let analyser = null;
  let meterFrame = 0;
  let lastMeterUpdate = 0;
  let recordingStartedAt = 0;
  let recordingElapsed = 0;
  let chunks = [];
  let samples = [];
  let liveWaveform = Array(WAVEFORM_BARS).fill(0.12);
  let recordedWaveform = Array(WAVEFORM_BARS).fill(0.12);
  let recordedBlob = null;
  let recordedUrl = '';
  let recordedMime = '';
  let recordedDuration = 0;
  let previewPlaying = false;
  let inlineToolMenuOpen = false;
  let holdTimer = null;
  let holdRecording = false;
  let ignoreNextClick = false;
  let ignoreNextSendClick = false;
  let stopAfterStart = false;
  let discardAfterStop = false;
  let destroyed = false;

  $: recording = recorderState === 'recording' || recorderState === 'requesting';
  $: busy = uploading || locating || recorderState === 'uploading' || recorderState === 'processing';
  $: displayDuration = recorderState === 'recording' ? recordingElapsed : recordedDuration;
  $: useExternalToolMenu = typeof onShowToolMenu === 'function';

  function handleTyping() {
    if (!isTyping) {
      isTyping = true;
      sendTyping('start');
    }

    clearTimeout(typingTimeout);
    typingTimeout = setTimeout(stopTyping, 1500);
  }

  function stopTyping() {
    clearTimeout(typingTimeout);
    if (isTyping) sendTyping('stop');
    isTyping = false;
  }

  function focusMessageInput() {
    requestAnimationFrame(() => {
      textInput?.focus({ preventScroll: true });
    });
  }

  function commitSend({ keepFocus = false } = {}) {
    if (sending || busy || !text.trim() || disabled) return;

    sending = true;
    sendMessage({
      type: 'chat',
      conversationId: get(conversationId),
      from: get(myId),
      message: text.trim(),
      replyTo: get(replyingTo)?.id || null,
      msgType: hasLink(text.trim()) ? 'link' : 'text'
    });

    text = '';
    replyingTo.set(null);
    stopTyping();

    requestAnimationFrame(() => {
      sending = false;
      if (keepFocus) focusMessageInput();
    });
  }

  function onKeyDown(event) {
    if (event.key === 'Enter' && !event.shiftKey && !event.repeat && !event.isComposing) {
      event.preventDefault();
      commitSend();
    }
  }

  function handleSendPointerDown(event) {
    if (busy || !text.trim() || disabled) return;
    event.preventDefault();
    ignoreNextSendClick = true;
    commitSend({ keepFocus: true });
    setTimeout(() => {
      ignoreNextSendClick = false;
    }, 0);
  }

  function handleSendClick(event) {
    if (ignoreNextSendClick) {
      event.preventDefault();
      ignoreNextSendClick = false;
      return;
    }
    commitSend({ keepFocus: true });
  }

  export function pickImages() {
    closeInlineToolMenu();
    imageInput?.click();
  }

  export function pickFiles() {
    closeInlineToolMenu();
    fileInput?.click();
  }

  function getLocationErrorMessage(error) {
    if (error?.code === 1) return 'Bạn đã từ chối quyền định vị. Vui lòng cấp quyền và thử lại.';
    if (error?.code === 2) return 'Không thể xác định vị trí hiện tại. Vui lòng thử lại.';
    if (error?.code === 3) return 'Yêu cầu định vị đã hết thời gian. Vui lòng thử lại.';
    return 'Không thể lấy vị trí. Vui lòng kiểm tra quyền định vị và thử lại.';
  }

  export function sendCurrentLocation() {
    closeInlineToolMenu();
    if (disabled || busy) return;

    if (!navigator.geolocation) {
      notify.error('Trình duyệt không hỗ trợ định vị.');
      return;
    }

    locating = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude, accuracy } = position.coords;
          const sent = sendMessage({
            type: 'chat',
            conversationId: get(conversationId),
            from: get(myId),
            msgType: 'location',
            message: JSON.stringify({
              lat: latitude,
              lng: longitude,
              accuracy,
              timestamp: position.timestamp || Date.now()
            }),
            replyTo: get(replyingTo)?.id || null
          });

          if (!sent) throw new Error('WebSocket is not connected');
          replyingTo.set(null);
        } catch (error) {
          console.error('Send location failed:', error);
          notify.error('Không thể gửi vị trí. Vui lòng kiểm tra kết nối và thử lại.');
        } finally {
          locating = false;
        }
      },
      (error) => {
        locating = false;
        notify.error(getLocationErrorMessage(error));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000
      }
    );
  }

  function openStickerPicker() {
    closeInlineToolMenu();
    onShowStickerPicker?.();
  }

  function getUploadErrorMessage(error, label = 'tệp') {
    const message = String(error?.message || '').trim();
    if (message.includes('storage service upload failed') || message.includes('relation "conversations" does not exist')) {
      return `Không thể upload ${label} lúc này. Vui lòng thử lại.`;
    }
    if (message) return message;
    return `Không thể upload ${label}. Vui lòng kiểm tra kết nối và thử lại.`;
  }

  async function handleUploadAndSend(files) {
    if (!files.length || busy) return;
    uploading = true;

    try {
      for (const file of files) {
        const uploaded = await uploadFile(file);
        const sent = sendMessage({
          type: 'chat',
          conversationId: get(conversationId),
          from: get(myId),
          message: uploaded.url,
          msgType: 'media',
          meta: {
            mediaType: uploaded.type,
            name: uploaded.name,
            size: uploaded.size,
            mime: uploaded.mime
          },
          replyTo: get(replyingTo)?.id || null
        });
        if (!sent) throw new Error('Kết nối trò chuyện chưa sẵn sàng. Tệp sẽ không được gửi.');
      }
      replyingTo.set(null);
    } catch (error) {
      console.error('Upload failed:', error);
      notify.error(getUploadErrorMessage(error, files.length > 1 ? 'các tệp' : 'tệp'));
    } finally {
      uploading = false;
    }
  }

  function onFilesSelected(event) {
    const files = Array.from(event.target.files || []);
    void handleUploadAndSend(files);
    event.target.value = '';
  }

  async function onImagesSelected(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    if (files.length === 1) {
      await handleUploadAndSend(files);
      event.target.value = '';
      return;
    }

    uploading = true;
    try {
      const result = await uploadImages(files);
      const urls = result.images.map((image) => image.url);

      sendMessage({
        type: 'chat',
        conversationId: get(conversationId),
        from: get(myId),
        msgType: 'album',
        message: JSON.stringify({ urls, fullUrls: urls }),
        replyTo: get(replyingTo)?.id || null
      });
      replyingTo.set(null);
    } catch (error) {
      console.error('Upload gallery failed:', error);
      notify.error(getUploadErrorMessage(error, 'hình ảnh'));
    } finally {
      uploading = false;
      event.target.value = '';
    }
  }

  function getSupportedMimeType() {
    if (typeof MediaRecorder === 'undefined') return '';
    return (
      ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4'].find((type) =>
        MediaRecorder.isTypeSupported?.(type)
      ) || ''
    );
  }

  function getVoiceExtension(mime = '') {
    if (mime.includes('ogg')) return 'ogg';
    if (mime.includes('mp4')) return 'm4a';
    return 'webm';
  }

  function formatDuration(value = 0) {
    const seconds = Math.max(0, Math.round(value));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  function describeReply(reply) {
    if (!reply) return '';
    if (reply.type === 'location') return 'Vị trí hiện tại';
    if (reply.type === 'audio') return 'Tin nhắn thoại';
    if (reply.type === 'media') return 'Tệp đính kèm';
    if (reply.type === 'album') return 'Bộ ảnh';
    return reply.content || '';
  }

  export function openVoiceRecorder() {
    if (disabled || busy) return;
    closeInlineToolMenu();
    stopTyping();
    voiceMode = true;
    recorderError = '';
  }

  function closeVoiceRecorder() {
    if (busy) return;
    cancelRecording();
    voiceMode = false;
  }

  async function startRecording() {
    if (disabled || busy || recorderState === 'recording' || recorderState === 'requesting') return;

    resetRecording();
    recorderError = '';

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      recorderError = 'Trình duyệt này chưa hỗ trợ ghi âm.';
      recorderState = 'error';
      return;
    }

    recorderState = 'requesting';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1
        }
      });

      if (destroyed || !voiceMode) {
        stream.getTracks().forEach((track) => track.stop());
        discardAfterStop = false;
        stopAfterStart = false;
        recorderState = 'idle';
        return;
      }

      mediaStream = stream;
      const mimeType = getSupportedMimeType();
      mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordedMime = mediaRecorder.mimeType || mimeType || 'audio/webm';
      chunks = [];
      samples = [];
      liveWaveform = Array(WAVEFORM_BARS).fill(0.12);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data?.size) chunks.push(event.data);
      };
      mediaRecorder.onerror = () => {
        recorderError = 'Ghi âm bị gián đoạn. Vui lòng thử lại.';
        recorderState = 'error';
        releaseMicrophone();
      };
      mediaRecorder.onstop = finalizeRecording;

      setupMeter(stream);
      recordingStartedAt = Date.now();
      recordingElapsed = 0;
      recorderState = 'recording';
      mediaRecorder.start(250);
      updateMeter();

      if (stopAfterStart) {
        stopAfterStart = false;
        stopRecording();
      }
    } catch (error) {
      recorderState = 'error';
      recorderError =
        error?.name === 'NotAllowedError'
          ? 'Bạn cần cho phép dùng micro để gửi tin nhắn thoại.'
          : 'Không thể mở micro. Hãy kiểm tra thiết bị và thử lại.';
      releaseMicrophone();
    }
  }

  function setupMeter(stream) {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      audioContext = new AudioContextClass();
      if (audioContext.state === 'suspended') void audioContext.resume();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.72;
      audioContext.createMediaStreamSource(stream).connect(analyser);
    } catch {
      analyser = null;
    }
  }

  function updateMeter(timestamp = 0) {
    if (recorderState !== 'recording') return;

    if (Date.now() - recordingStartedAt >= MAX_RECORDING_SECONDS * 1000) {
      stopRecording();
      return;
    }

    if (timestamp - lastMeterUpdate > 80) {
      recordingElapsed = Math.min(MAX_RECORDING_SECONDS, (Date.now() - recordingStartedAt) / 1000);
      let level = 0.16;
      if (analyser) {
        const values = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(values);
        level = Math.max(0.1, Math.min(1, values.reduce((sum, value) => sum + value, 0) / values.length / 100));
      }
      samples = [...samples, level];
      liveWaveform = [...liveWaveform.slice(1), level];
      lastMeterUpdate = timestamp;
    }

    meterFrame = requestAnimationFrame(updateMeter);
  }

  function stopRecording() {
    if (recorderState === 'requesting') {
      stopAfterStart = true;
      return;
    }
    if (recorderState !== 'recording' || !mediaRecorder) return;

    recorderState = 'processing';
    cancelAnimationFrame(meterFrame);
    try {
      mediaRecorder.stop();
    } catch {
      recorderError = 'Không thể hoàn tất bản ghi âm.';
      recorderState = 'error';
      releaseMicrophone();
    }
  }

  function compressWaveform(values) {
    if (!values.length) return Array(WAVEFORM_BARS).fill(0.12);
    return Array.from({ length: WAVEFORM_BARS }, (_, index) => {
      const start = Math.floor((index * values.length) / WAVEFORM_BARS);
      const end = Math.max(start + 1, Math.floor(((index + 1) * values.length) / WAVEFORM_BARS));
      const bucket = values.slice(start, end);
      return Number(Math.max(0.1, ...bucket).toFixed(2));
    });
  }

  function finalizeRecording() {
    const duration = Math.min(MAX_RECORDING_SECONDS, (Date.now() - recordingStartedAt) / 1000);
    const blob = new Blob(chunks, { type: recordedMime || chunks[0]?.type || 'audio/webm' });
    releaseMicrophone();

    if (discardAfterStop) {
      discardAfterStop = false;
      resetRecording();
      return;
    }

    if (duration < MIN_RECORDING_SECONDS || blob.size === 0) {
      recorderError = 'Bản ghi quá ngắn. Hãy ghi ít nhất 1 giây.';
      recorderState = 'error';
      return;
    }

    recordedBlob = blob;
    recordedDuration = duration;
    recordedWaveform = compressWaveform(samples);
    recordedUrl = URL.createObjectURL(blob);
    recorderState = 'preview';
  }

  function releaseMicrophone() {
    cancelAnimationFrame(meterFrame);
    meterFrame = 0;
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = null;
    analyser = null;
    if (audioContext && audioContext.state !== 'closed') void audioContext.close();
    audioContext = null;
    mediaRecorder = null;
  }

  function resetRecording() {
    releaseMicrophone();
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    recordedUrl = '';
    recordedBlob = null;
    recordedDuration = 0;
    recordingElapsed = 0;
    recordedMime = '';
    previewPlaying = false;
    chunks = [];
    samples = [];
    liveWaveform = Array(WAVEFORM_BARS).fill(0.12);
    recordedWaveform = Array(WAVEFORM_BARS).fill(0.12);
    discardAfterStop = false;
    stopAfterStart = false;
    recorderState = 'idle';
  }

  function cancelRecording() {
    if (recorderState === 'recording' || recorderState === 'requesting') {
      discardAfterStop = true;
      stopRecording();
      if (recorderState === 'requesting') {
        stopAfterStart = true;
      }
      return;
    }
    resetRecording();
    recorderError = '';
  }

  function toggleToolMenu() {
    if (busy || disabled) return;
    if (useExternalToolMenu) {
      onShowToolMenu?.();
      return;
    }
    inlineToolMenuOpen = !inlineToolMenuOpen;
  }

  function closeInlineToolMenu() {
    inlineToolMenuOpen = false;
  }

  function handleToolAction(action) {
    closeInlineToolMenu();
    if (action === 'sticker') openStickerPicker();
    if (action === 'files') pickFiles();
    if (action === 'images') pickImages();
    if (action === 'voice') openVoiceRecorder();
    if (action === 'location') sendCurrentLocation();
  }

  async function sendVoiceMessage() {
    if (!recordedBlob || recorderState !== 'preview' || disabled) return;

    recorderState = 'uploading';
    recorderError = '';
    try {
      const extension = getVoiceExtension(recordedMime);
      const file = new File([recordedBlob], `voice-${Date.now()}.${extension}`, {
        type: recordedMime || recordedBlob.type || 'audio/webm'
      });
      const uploaded = await uploadFile(file);
      const sent = sendMessage({
        type: 'chat',
        conversationId: get(conversationId),
        from: get(myId),
        msgType: 'audio',
        message: JSON.stringify({
          url: uploaded.url,
          duration: Number(recordedDuration.toFixed(2)),
          mime: uploaded.mime || file.type,
          size: uploaded.size || file.size,
          waveform: recordedWaveform
        }),
        replyTo: get(replyingTo)?.id || null
      });

      if (!sent) throw new Error('WebSocket is not connected');

      replyingTo.set(null);
      resetRecording();
      voiceMode = false;
    } catch (error) {
      console.error('Voice message failed:', error);
      recorderError = 'Không thể gửi bản ghi âm. Vui lòng kiểm tra kết nối và thử lại.';
      recorderState = 'preview';
    }
  }

  function handleRecordPointerDown() {
    if (recorderState !== 'idle' && recorderState !== 'error') return;
    clearTimeout(holdTimer);
    holdTimer = setTimeout(async () => {
      holdRecording = true;
      ignoreNextClick = true;
      await startRecording();
    }, HOLD_DELAY_MS);
  }

  function handleRecordPointerUp() {
    clearTimeout(holdTimer);
    if (!holdRecording) return;
    holdRecording = false;
    stopRecording();
    setTimeout(() => {
      ignoreNextClick = false;
    }, 0);
  }

  function handleRecordClick() {
    if (ignoreNextClick) {
      ignoreNextClick = false;
      return;
    }
    if (recorderState === 'recording' || recorderState === 'requesting') stopRecording();
    else if (recorderState === 'idle' || recorderState === 'error') void startRecording();
  }

  async function togglePreview() {
    if (!previewAudio) return;
    if (previewAudio.paused) await previewAudio.play();
    else previewAudio.pause();
  }

  function handleWindowPointerDown(event) {
    if (useExternalToolMenu || !inlineToolMenuOpen) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (toolMenuButton?.contains(target) || (target instanceof Element && target.closest('.tool-menu'))) return;
    closeInlineToolMenu();
  }

  function handleWindowKeyDown(event) {
    if (event.key === 'Escape') closeInlineToolMenu();
  }

  onMount(() => {
    window.addEventListener('pointerdown', handleWindowPointerDown, true);
    window.addEventListener('keydown', handleWindowKeyDown, true);

    return () => {
      window.removeEventListener('pointerdown', handleWindowPointerDown, true);
      window.removeEventListener('keydown', handleWindowKeyDown, true);
    };
  });

  onDestroy(() => {
    destroyed = true;
    clearTimeout(typingTimeout);
    clearTimeout(holdTimer);
    if (isTyping) sendTyping('stop');
    discardAfterStop = true;
    releaseMicrophone();
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
  });
</script>

<div class:voice-open={voiceMode} class="composer-root">
  {#if !voiceMode}
    <div class="action-bar">
      <button type="button" aria-label="Mở sticker" on:click={openStickerPicker} disabled={busy}>
        <Smile size="21" />
      </button>
      <button type="button" aria-label="Đính kèm tệp" on:click={pickFiles} disabled={busy}>
        <Paperclip size="21" />
      </button>
      <button type="button" aria-label="Gửi ảnh" on:click={pickImages} disabled={busy}>
        <ImageIcon size="21" />
      </button>
      <div class="tool-menu-wrap">
        <button
          type="button"
          bind:this={toolMenuButton}
          class="tool-menu-trigger"
          aria-label="Mở thêm công cụ"
          aria-expanded={useExternalToolMenu ? toolMenuOpen : inlineToolMenuOpen}
          aria-haspopup="menu"
          on:click|stopPropagation={toggleToolMenu}
          disabled={busy || disabled}>
          <MoreVertical size="21" />
        </button>

        {#if !useExternalToolMenu && inlineToolMenuOpen}
          <div class="tool-menu" role="menu" aria-label="Công cụ tin nhắn">
            <button type="button" role="menuitem" on:click={() => handleToolAction('sticker')} disabled={busy}>
              <Smile size="18" />
              <span>Sticker</span>
            </button>
            <button type="button" role="menuitem" on:click={() => handleToolAction('images')} disabled={busy}>
              <ImageIcon size="18" />
              <span>Ảnh</span>
            </button>
            <button type="button" role="menuitem" on:click={() => handleToolAction('files')} disabled={busy}>
              <Paperclip size="18" />
              <span>Tệp</span>
            </button>
            <button
              type="button"
              class="tool-menu-item--voice"
              role="menuitem"
              on:click={() => handleToolAction('voice')}
              disabled={busy || disabled}>
              <Mic size="18" />
              <span>Ghi âm</span>
            </button>
            <button
              type="button"
              class="tool-menu-item--location"
              role="menuitem"
              on:click={() => handleToolAction('location')}
              disabled={busy || disabled}>
              {#if locating}<LoaderCircle class="location-spinner" size="18" />{:else}<MapPin size="18" />{/if}
              <span>{locating ? 'Đang định vị...' : 'Gửi vị trí'}</span>
            </button>
          </div>
        {/if}
      </div>
    </div>

    <div class="input-section">
      {#if $replyingTo}
        <div class="reply-strip">
          <div class="truncate">
            <strong>{$replyingTo.senderId === $myId ? 'Bạn' : 'Người khác'}</strong>
            <span>· {describeReply($replyingTo)}</span>
          </div>
          <button aria-label="Huỷ trả lời" on:click={() => replyingTo.set(null)}><X size="15" /></button>
        </div>
      {/if}

      <div class="input-row">
        <textarea
          bind:this={textInput}
          bind:value={text}
          rows="1"
          {placeholder}
          {disabled}
          autofocus={autoFocus}
          on:keydown={onKeyDown}
          on:input={handleTyping}>
        </textarea>

        <button
          type="button"
          class="send-button"
          aria-label={text.trim() ? 'Gửi tin nhắn' : 'Nhập nội dung để gửi'}
          on:pointerdown={handleSendPointerDown}
          on:click={handleSendClick}
          disabled={busy || !text.trim() || disabled}>
          <Send size="20" />
        </button>
      </div>

      {#if uploading}
        <div class="status-line">Đang tải tệp...</div>
      {:else if locating}
        <div class="status-line">Đang lấy vị trí hiện tại...</div>
      {/if}
    </div>
  {:else}
    <section class="voice-panel" aria-label="Ghi tin nhắn thoại">
      <div class="voice-heading">
        <div>
          <strong>Tin nhắn thoại</strong>
          <span>{recorderState === 'preview' ? 'Nghe lại trước khi gửi' : 'Chạm hoặc nhấn giữ để ghi âm'}</span>
        </div>
        <button
          type="button"
          class="close-button"
          aria-label="Đóng ghi âm"
          on:click={closeVoiceRecorder}
          disabled={busy}>
          <X size="18" />
        </button>
      </div>

      <div class:recording class="voice-visual">
        <span class="duration">{formatDuration(displayDuration)}</span>
        <div class="waveform" aria-hidden="true">
          {#each recorderState === 'preview' ? recordedWaveform : liveWaveform as level}
            <i style={`height: ${Math.max(10, level * 100)}%`}></i>
          {/each}
        </div>
      </div>

      <div class="voice-controls">
        {#if recorderState === 'preview'}
          <button type="button" class="secondary-control danger" aria-label="Xoá bản ghi" on:click={cancelRecording}>
            <Trash2 size="19" />
          </button>
          <button
            type="button"
            class="record-control preview"
            aria-label={previewPlaying ? 'Tạm dừng' : 'Phát bản ghi'}
            on:click={togglePreview}>
            {#if previewPlaying}<Pause size="27" />{:else}<Play size="27" fill="currentColor" />{/if}
          </button>
          <button type="button" class="secondary-control send" aria-label="Gửi bản ghi" on:click={sendVoiceMessage}>
            <Send size="19" />
          </button>
          <audio
            class="hidden-audio"
            bind:this={previewAudio}
            src={recordedUrl}
            on:play={() => (previewPlaying = true)}
            on:pause={() => (previewPlaying = false)}
            on:ended={() => (previewPlaying = false)}>
          </audio>
        {:else}
          <button
            type="button"
            class:active={recording}
            class="record-control"
            aria-label={recording ? 'Dừng ghi âm' : 'Bắt đầu ghi âm'}
            disabled={recorderState === 'processing'}
            on:pointerdown={handleRecordPointerDown}
            on:pointerup={handleRecordPointerUp}
            on:pointercancel={handleRecordPointerUp}
            on:pointerleave={() => holdRecording && handleRecordPointerUp()}
            on:click={handleRecordClick}>
            <Mic size="29" />
          </button>
        {/if}
      </div>

      {#if recorderState === 'requesting'}
        <p class="voice-hint">Đang chờ quyền truy cập micro...</p>
      {:else if recorderState === 'recording'}
        <p class="voice-hint live">
          <span></span>
          Đang ghi âm · chạm lần nữa để dừng
        </p>
      {:else if recorderState === 'uploading'}
        <p class="voice-hint">Đang gửi bản ghi âm...</p>
      {:else}
        <p class="voice-hint">Bấm một lần để ghi liên tục, hoặc nhấn giữ rồi thả để hoàn tất</p>
      {/if}

      {#if recorderError}
        <p class="voice-error" role="alert">{recorderError}</p>
      {/if}
    </section>
  {/if}

  <input
    type="file"
    accept="image/*"
    multiple
    class="hidden-input"
    bind:this={imageInput}
    on:change={onImagesSelected} />
  <input type="file" multiple class="hidden-input" bind:this={fileInput} on:change={onFilesSelected} />
</div>

<style>
  .composer-root {
    position: relative;
    display: flex;
    z-index: 20;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    border-top: 1px solid rgba(226, 232, 240, 0.78);
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(22px);
  }

  button {
    touch-action: manipulation;
  }

  .action-bar {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.34rem 0.7rem 0.1rem;
    color: #64748b;
  }

  .action-bar button,
  .close-button,
  .tool-menu-trigger {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    border-radius: 999px;
    transition:
      color 120ms ease,
      background 120ms ease,
      transform 120ms ease;
  }

  .action-bar button:hover,
  .close-button:hover,
  .tool-menu-trigger:hover {
    color: #0f6fff;
    background: #eff6ff;
  }

  .action-bar button:active,
  .close-button:active,
  .record-control:active,
  .secondary-control:active {
    transform: scale(0.94);
  }

  .tool-menu-wrap {
    margin-left: auto;
    position: relative;
    z-index: 80;
  }

  .tool-menu-trigger {
    color: #0f6fff;
  }

  .tool-menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + 0.45rem);
    z-index: 90;
    display: grid;
    gap: 0.2rem;
    min-width: 10rem;
    padding: 0.35rem;
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.12);
    backdrop-filter: blur(18px);
  }

  .tool-menu button {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    height: 2.2rem;
    padding: 0 0.7rem;
    border-radius: 0.75rem;
    color: #334155;
    text-align: left;
    white-space: nowrap;
    background: transparent;
  }

  .tool-menu button:hover {
    color: #0f172a;
    background: #eff6ff;
  }

  .tool-menu button span {
    font-size: 0.78rem;
    font-weight: 600;
  }

  .input-section {
    display: grid;
    gap: 0.35rem;
    min-height: 0;
    padding: 0.1rem 0.75rem 0.5rem;
  }

  .input-row {
    display: flex;
    align-items: flex-end;
    gap: 0.45rem;
  }

  textarea {
    min-height: 2.35rem;
    max-height: 6rem;
    flex: 1;
    resize: none;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 1.1rem;
    padding: 0.58rem 0.9rem;
    color: #0f172a;
    background: rgba(248, 250, 252, 0.9);
    font-size: 0.84rem;
    line-height: 1.25rem;
    outline: none;
  }

  textarea:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .send-button {
    display: grid;
    place-items: center;
    width: 2.35rem;
    height: 2.35rem;
    flex: 0 0 auto;
    border-radius: 999px;
    color: white;
    background: linear-gradient(145deg, #1687ff, #0868e8);
    box-shadow: 0 7px 18px rgba(15, 111, 255, 0.24);
  }

  .reply-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 2rem;
    padding: 0.35rem 0.5rem 0.35rem 0.7rem;
    border-left: 3px solid #2583f8;
    border-radius: 0.55rem;
    color: #64748b;
    background: #f1f5f9;
    font-size: 0.72rem;
  }

  .reply-strip button {
    display: grid;
    place-items: center;
    width: 1.6rem;
    height: 1.6rem;
    flex: 0 0 auto;
    border-radius: 999px;
  }

  .status-line {
    padding-left: 0.5rem;
    color: #64748b;
    font-size: 0.68rem;
  }

  .voice-panel {
    display: grid;
    height: 100%;
    min-height: 0;
    grid-template-columns: minmax(9rem, 0.7fr) minmax(10rem, 1.5fr) auto;
    grid-template-rows: 1fr auto;
    align-items: center;
    gap: 0.2rem 0.9rem;
    padding: 0.45rem 0.8rem;
    background: linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(239, 246, 255, 0.94));
  }

  .voice-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  .voice-heading > div {
    display: grid;
    min-width: 0;
  }

  .voice-heading strong {
    color: #0f172a;
    font-size: 0.78rem;
  }

  .voice-heading span {
    overflow: hidden;
    color: #64748b;
    font-size: 0.62rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .close-button {
    order: -1;
    flex: 0 0 auto;
    color: #64748b;
  }

  .voice-visual {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    min-width: 0;
    height: 2.5rem;
    padding: 0.35rem 0.65rem;
    border: 1px solid rgba(147, 197, 253, 0.42);
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.78);
  }

  .duration {
    width: 2.7rem;
    flex: 0 0 auto;
    color: #334155;
    font-variant-numeric: tabular-nums;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .waveform {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
    height: 1.45rem;
    overflow: hidden;
  }

  .waveform i {
    width: 3px;
    min-height: 3px;
    flex: 1 1 3px;
    border-radius: 999px;
    background: #4798ff;
    transition: height 80ms linear;
  }

  .voice-visual.recording .waveform i {
    background: #f43f5e;
  }

  .voice-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
  }

  .record-control,
  .secondary-control {
    display: grid;
    place-items: center;
    border-radius: 999px;
    transition:
      transform 120ms ease,
      box-shadow 120ms ease,
      background 120ms ease;
  }

  .record-control {
    width: 3.15rem;
    height: 3.15rem;
    color: white;
    background: linear-gradient(145deg, #1687ff, #0868e8);
    box-shadow: 0 10px 28px rgba(15, 111, 255, 0.32);
  }

  .record-control.active {
    background: linear-gradient(145deg, #fb7185, #e11d48);
    box-shadow:
      0 0 0 6px rgba(244, 63, 94, 0.12),
      0 10px 28px rgba(225, 29, 72, 0.28);
    animation: record-pulse 1.45s ease-in-out infinite;
  }

  .record-control.preview {
    padding-left: 0.15rem;
  }

  .secondary-control {
    width: 2.25rem;
    height: 2.25rem;
    color: #475569;
    background: white;
    box-shadow: 0 5px 15px rgba(15, 23, 42, 0.1);
  }

  .secondary-control.danger {
    color: #e11d48;
  }
  .secondary-control.send {
    color: white;
    background: #0f7af5;
  }

  .voice-hint,
  .voice-error {
    grid-column: 1 / -1;
    margin: 0;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.62rem;
  }

  .voice-hint {
    color: #64748b;
  }
  .voice-hint.live {
    color: #e11d48;
  }
  .voice-hint.live span {
    display: inline-block;
    width: 0.38rem;
    height: 0.38rem;
    border-radius: 999px;
    background: #f43f5e;
  }
  .voice-error {
    color: #dc2626;
  }

  .hidden-input,
  .hidden-audio {
    display: none;
  }

  button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  :global(.location-spinner) {
    animation: location-spin 0.8s linear infinite;
  }

  @keyframes location-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes record-pulse {
    50% {
      transform: scale(1.04);
    }
  }

  @media (max-width: 640px) {
    .composer-root {
      height: auto;
      max-height: min(56dvh, 28rem);
    }

    .action-bar {
      gap: 0.15rem;
      padding: 0.28rem 0.55rem 0.08rem;
    }

    .action-bar button,
    .close-button,
    .tool-menu-trigger {
      width: 1.95rem;
      height: 1.95rem;
    }

    .tool-menu {
      min-width: 9.5rem;
    }

    .tool-menu button:not(.tool-menu-item--voice):not(.tool-menu-item--location) {
      display: none;
    }

    .tool-menu-item--voice,
    .tool-menu-item--location {
      min-width: 9rem;
      justify-content: flex-start;
    }

    .input-section {
      padding: 0.48rem 0.65rem 0.55rem;
    }

    textarea {
      min-height: 2.55rem;
      border-radius: 1.3rem;
      padding: 0.65rem 0.95rem;
      font-size: 0.92rem;
    }

    .send-button {
      width: 2.55rem;
      height: 2.55rem;
    }

    .voice-panel {
      min-height: 19rem;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto auto auto;
      justify-items: center;
      gap: 0.85rem;
      padding: 0.8rem 1rem max(1rem, env(safe-area-inset-bottom));
    }

    .voice-heading {
      width: 100%;
      justify-content: center;
    }

    .voice-heading > div {
      flex: 1;
      text-align: center;
    }

    .voice-heading strong {
      font-size: 0.94rem;
    }
    .voice-heading span {
      font-size: 0.72rem;
    }

    .close-button {
      position: absolute;
      top: 0.7rem;
      right: 0.7rem;
      order: 0;
    }

    .voice-visual {
      width: min(100%, 28rem);
      height: 3.5rem;
      border-radius: 1.2rem;
    }

    .waveform {
      height: 2rem;
    }
    .waveform i {
      width: 2px;
    }

    .record-control {
      width: 5.2rem;
      height: 5.2rem;
    }

    .secondary-control {
      width: 2.8rem;
      height: 2.8rem;
    }

    .voice-controls {
      gap: 1rem;
    }
    .voice-hint,
    .voice-error {
      font-size: 0.72rem;
      white-space: normal;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .record-control.active {
      animation: none;
    }
    * {
      transition-duration: 0.01ms !important;
    }
  }
</style>
